import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import PublishModal from '../components/PublishModal'
import HomeSidebar, { PENDING_PUBLIC_ID } from '../components/HomeSidebar'
import { useAuth } from '../contexts/AuthContext'
import {
  listSkills, listAllProjects,
  listProjectSkills, addSkillToProject, removeSkillFromProject,
  archiveSkill, permanentlyDeleteSkill, createSkill, createProject, updateProject, getHistory,
  getPreferences, updatePreferences,
  getSkill, listOrgs, listOrgMembers, listOrgInvitations, inviteOrgMember,
  listProjectMembers, addProjectMember, removeProjectMember,
} from '../api'
import { memberName, memberInitials, isPendingMember } from '../lib/memberName'

const WHATS_NEW_VERSION = '2026-06-02'

// ─── Types ───────────────────────────────────────────────────────────────────

interface SkillRow {
  id: string; name: string; slug?: string; version: string; status: string
  description?: string; tags?: string[]; file_type?: string; owner?: string
  owner_id?: string; updated_at?: string; project_id?: string | null
}

interface Project { id: string; name: string; skill_count: number; is_public?: boolean; owner_id?: string }
interface Group { team_id: string; team_name: string; projects: Project[] }
interface Member {
  id?: string; user_id: string; display_name?: string; email?: string; role?: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, React.CSSProperties> = {
  production: { background: 'var(--success-bg)', color: 'var(--success-text)' },
  staging:    { background: 'var(--warning-bg)', color: 'var(--warning-text)' },
  draft:      { background: 'rgba(107,114,128,0.10)', color: '#4b5563' },
  archived:   { background: 'rgba(239,68,68,0.08)', color: '#ef4444' },
}
const statusStyle = (s: string) => STATUS_STYLE[s] || STATUS_STYLE.draft
const statusLabel = (s: string) => s === 'production' ? '✓ Production' : s === 'staging' ? '~ Staging' : '– Draft'
const AVATAR_PALETTE = ['var(--mitel-mid-blue, #0073d0)','var(--mitel-purple, #7c3aed)','var(--mitel-green, #0d9488)','var(--mitel-gray-70, #475569)','var(--mitel-light-blue, #38bdf8)']

function timeAgo(iso?: string) {
  if (!iso) return '—'
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24); if (d < 8) return `${d}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function initials(name?: string, email?: string) {
  if (name?.trim()) { const p = name.trim().split(/\s+/); return p.length > 1 ? (p[0][0]+p[p.length-1][0]).toUpperCase() : p[0].slice(0,2).toUpperCase() }
  return (email || '??').slice(0,2).toUpperCase()
}

const AV_COLORS = ['#667085','#4573d2','#e8890c','#a67cdb','#059669','#dc2626']
function avColor(str: string) { let h=0; for(const c of str) h=(h*31+c.charCodeAt(0))&0xffffffff; return AV_COLORS[Math.abs(h)%AV_COLORS.length] }

function Avatar({ name, email, size = 28 }: { name?: string; email?: string; size?: number }) {
  const ini = initials(name, email)
  const bg  = avColor(name || email || '?')
  return <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.36, fontWeight: 700, flexShrink: 0 }}>{ini}</div>
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate   = useNavigate()
  const { user, signOut } = useAuth()

  // View
  const [view, setView] = useState<'projects' | 'team'>('projects')
  const [teamContext, setTeamContext] = useState<{ id: string; name: string } | null>(null)

  // Projects data
  const [groups, setGroups]               = useState<Group[]>([])
  const [selectedProject, setSelectedProject] = useState<string>('') // project id or 'unassigned'
  const [skills, setSkills]               = useState<SkillRow[]>([])
  const [, setUnassignedCount] = useState(0)
  const [totalSkills, setTotalSkills] = useState(0)
  const [skillsLoading, setSkillsLoading] = useState(false)
  const [newSkillAnimations, setNewSkillAnimations] = useState<Map<string, number>>(new Map())
  const loadIdRef = useRef(0)
  const dragSkillRef = useRef<string[]>([])
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const BATCH_SIZE = 5

  // Team data
  const [members, setMembers]         = useState<Member[]>([])
  const [teamSection] = useState('all-members')
  const [selectedUser, setSelectedUser] = useState<Member | null>(null)
  const [teamLoading, setTeamLoading] = useState(false)
  // True until the initial listOrgs → loadMembers chain settles (success, no
  // org, or failure) — drives the nav-bar avatar-cluster skeleton so it never
  // renders as an empty/missing cluster while members are still in flight.
  const [membersLoading, setMembersLoading] = useState(true)
  const [orgId, setOrgId] = useState<string | null>(null)
  const [orgInvitations, setOrgInvitations] = useState<{id:string;email:string;role:string;created_at?:string}[]>([])

  // P3 skill detail
  const [selectedSkill, setSelectedSkill] = useState<SkillRow | null>(null)
  const [skillHistory, setSkillHistory]   = useState<any[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Filters / search
  const [filter, setFilter]   = useState<'all' | 'production' | 'staging' | 'draft'>('all')
  const [sort, setSort]       = useState<'default' | 'name' | 'status'>('default')
  const [search, setSearch]   = useState('')

  // Modals
  const [showWhatsNew, setShowWhatsNew]   = useState(false)
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set())
  const [draggingInFlight] = useState<Set<string>>(new Set())
  // Project edit
  const [projectMenu, setProjectMenu]           = useState<{ id: string } | null>(null)
  const [editProjectModal, setEditProjectModal] = useState<{ id: string; name: string; description: string } | null>(null)
  const [editProjectSaving, setEditProjectSaving] = useState(false)
  const [showNewSkill, setShowNewSkill]   = useState(false)
  const [showNewProject, setShowNewProject] = useState(false)
  const [showInvite, setShowInvite]       = useState(false)
  const [showMembersPanel, setShowMembersPanel] = useState(false)
  const [showFolderMembersPanel, setShowFolderMembersPanel] = useState(false)
  const [folderMembers, setFolderMembers] = useState<Member[]>([])
  const [folderMembersLoading, setFolderMembersLoading] = useState(false)
  const [folderMemberAdding, setFolderMemberAdding] = useState<string | null>(null)
  const [folderMemberRemoving, setFolderMemberRemoving] = useState<string | null>(null)
  const [showMoveWarn, setShowMoveWarn]   = useState(false)
  const [showProjPicker, setShowProjPicker] = useState(false)
  const [showPublish, setShowPublish]     = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  // Move / picker state
  const [moveTarget, setMoveTarget]           = useState<SkillRow | null>(null)
  const [pickerSearch, setPickerSearch]       = useState('')
  const [pickerSelected, setPickerSelected]   = useState<string | null>(null)
  const [pickerFrom, setPickerFrom]           = useState<string>('')
  const [pendingMove, setPendingMove]         = useState<{ skill: SkillRow; from: string; to: string } | null>(null)

  // New project form
  const [npName, setNpName]   = useState('')
  const [npDesc, setNpDesc]   = useState('')
  const [npSaving, setNpSaving] = useState(false)

  // Invite form
  const [invEmail, setInvEmail] = useState('')
  const [invRole, setInvRole]   = useState('editor')
  const [invSaving, setInvSaving] = useState(false)

  // Drop zone
  const [dzOver, setDzOver] = useState(false)

  // Toast
  const [toast, setToast] = useState('')
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const showToast = (msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current)
    setToast(msg)
    toastTimer.current = setTimeout(() => setToast(''), 3500)
  }

  const handleEditProjectSave = async () => {
    if (!editProjectModal) return
    setEditProjectSaving(true)
    try {
      const data: { name?: string; description?: string } = {}
      if (editProjectModal.name.trim()) data.name = editProjectModal.name.trim()
      if (editProjectModal.description.trim()) data.description = editProjectModal.description.trim()
      await updateProject(editProjectModal.id, data)
      setGroups(prev => prev.map(g => ({ ...g, projects: g.projects.map(p => p.id === editProjectModal.id ? { ...p, name: editProjectModal.name.trim() || p.name } : p) })))
      setEditProjectModal(null)
      showToast('Project updated')
    } catch { showToast('Failed to update project') }
    finally { setEditProjectSaving(false) }
  }

  const handleCloseWhatsNew = useCallback(() => {
    setShowWhatsNew(false)
    updatePreferences({ whats_new_last_seen: WHATS_NEW_VERSION }).catch(() => {})
  }, [])

  // ── Data loading ──────────────────────────────────────────────────────────

  // Which folder to land on once /projects/all resolves. Seeded from the last
  // folder the user actually had open (persisted in localStorage) so a reload
  // returns you where you were, instead of always bouncing back to Public.
  // First-ever visit (nothing stored yet) falls back to 'public' — Public
  // stays the default landing folder for a brand-new session. Also set when
  // the user clicks "Public" (via HomeSidebar's PENDING_PUBLIC_ID placeholder)
  // before /projects/all has resolved and the real Public project id is known.
  // A ref (not state) so loadGroups always reads the live value instead of a
  // stale closure. Cleared once honored by loadGroups, or by any other
  // explicit selection made before it resolves.
  const LAST_PROJECT_KEY = 'skilflow:lastProjectId'
  const pendingSelectionRef = useRef<string | null>((() => {
    try { return localStorage.getItem(LAST_PROJECT_KEY) || 'public' } catch { return 'public' }
  })())

  const handleSelectProject = useCallback((id: string) => {
    if (id === PENDING_PUBLIC_ID) {
      pendingSelectionRef.current = 'public'
      return
    }
    pendingSelectionRef.current = null
    setSelectedProject(id)
    try { localStorage.setItem(LAST_PROJECT_KEY, id) } catch {}
  }, [])

  const loadGroups = useCallback(async () => {
    try {
      const r = await listAllProjects()
      const g: Group[] = Array.isArray(r.data) ? r.data : []
      setGroups(g)
      const wanted = pendingSelectionRef.current
      if (wanted) {
        pendingSelectionRef.current = null
        const allProjs = g.flatMap(grp => grp.projects)
        if (wanted === 'unassigned') {
          setSelectedProject('unassigned')
          return
        }
        const target = wanted === 'public'
          ? allProjs.find(p => p.is_public)
          : allProjs.find(p => p.id === wanted)
        if (target) {
          // Land on the remembered folder — either Public as the default
          // landing folder, the last folder the user had open, or a click
          // already made before its row existed in the DOM.
          setSelectedProject(target.id)
          return
        }
        // The remembered folder no longer exists or isn't accessible anymore
        // (deleted, removed as a member, etc). Fall back to Public.
        const publicProj = allProjs.find(p => p.is_public)
        if (publicProj) {
          setSelectedProject(publicProj.id)
          return
        }
      }
      // Fall back to 'unassigned' if nothing has been selected yet (no Public
      // project exists for this org) — read live state via the functional
      // updater so a selection made while this request was in flight (e.g.
      // clicking another project before /projects/all resolves) is never
      // clobbered by this stale-closure callback.
      setSelectedProject(prev => (prev || !g.length) ? prev : 'unassigned')
    } catch {}
  }, [])

  const loadSkillsForProject = useCallback(async (projId: string) => {
    if (!projId) return
    const loadId = ++loadIdRef.current
    setSkillsLoading(true)
    setSelectedSkill(null)
    setSkillHistory([])
    setSkills([])
    setTotalSkills(0)
    setNewSkillAnimations(new Map())

    try {
      if (projId === 'unassigned') {
        // First batch of 5 — render immediately, animate in
        const r = await listSkills({ limit: BATCH_SIZE, offset: 0, unassigned: true })
        if (loadIdRef.current !== loadId) return
        const { items, total } = r.data as any
        const firstBatch: SkillRow[] = items || []
        setNewSkillAnimations(new Map(firstBatch.map((s: SkillRow, i: number) => [s.id, i])))
        setSkills(firstBatch)
        setTotalSkills(total || 0)
        setUnassignedCount(total || 0)
        setSkillsLoading(false)

        // Stream remaining batches of 5, each animating in on arrival
        let offset = BATCH_SIZE
        while (offset < (total || 0)) {
          if (loadIdRef.current !== loadId) return
          const br = await listSkills({ limit: BATCH_SIZE, offset, unassigned: true })
          if (loadIdRef.current !== loadId) return
          const batch: SkillRow[] = (br.data as any)?.items || []
          setNewSkillAnimations(new Map(batch.map((s: SkillRow, i: number) => [s.id, i])))
          setSkills(prev => [...prev, ...batch])
          offset += BATCH_SIZE
        }
      } else {
        const r = await listProjectSkills(projId)
        if (loadIdRef.current !== loadId) return
        setSkills(Array.isArray(r.data) ? r.data : (r.data?.data ?? []))
      }
    } catch { if (loadIdRef.current === loadId) setSkills([]) }
    finally { if (loadIdRef.current === loadId) setSkillsLoading(false) }
  }, [])

  const loadMembers = useCallback(async (oid?: string) => {
    const id = oid || orgId
    if (!id) return
    setTeamLoading(true)
    try {
      const r = await listOrgMembers(id)
      setMembers(Array.isArray(r.data) ? r.data : (r.data?.members ?? []))
    } catch {}
    finally { setTeamLoading(false); setMembersLoading(false) }
  }, [orgId])

  useEffect(() => {
    loadGroups()
    listOrgs().then(r => {
      const orgs = Array.isArray(r.data) ? r.data : []
      if (orgs.length) {
        const id = orgs[0].id
        setOrgId(id)
        loadMembers(id)
        listOrgInvitations(id).then(inv => {
          setOrgInvitations(Array.isArray(inv.data) ? inv.data : [])
        }).catch(() => {})
      } else {
        setMembersLoading(false)
      }
    }).catch(() => { setMembersLoading(false) })
    getPreferences().then(r => {
      const p = r.data || {}
      if (p.whats_new_muted !== true && p.whats_new_last_seen !== WHATS_NEW_VERSION) {
        setTimeout(() => setShowWhatsNew(true), 600)
      }
    }).catch(() => {})
  }, [loadGroups])

  useEffect(() => {
    if (!selectedProject) return
    loadSkillsForProject(selectedProject)
  }, [selectedProject, loadSkillsForProject])

  useEffect(() => {
    if (view === 'team' && !members.length) loadMembers()
  }, [view, members.length, loadMembers])

  // ── Skill actions ─────────────────────────────────────────────────────────

  const openSkill = async (sk: SkillRow) => {
    if (selectedSkill?.id === sk.id) { setSelectedSkill(null); return }
    setSelectedSkill(sk)
    setHistoryLoading(true)
    try {
      const r = await getHistory(sk.slug || sk.name)
      setSkillHistory(Array.isArray(r.data) ? r.data : [])
    } catch { setSkillHistory([]) }
    finally { setHistoryLoading(false) }
  }

  const openMovePicker = (sk: SkillRow) => {
    setMoveTarget(sk)
    setPickerFrom(selectedProject)
    setPickerSelected(null)
    setPickerSearch('')
    setShowProjPicker(true)
  }

  const confirmMove = async () => {
    if (!pickerSelected || !moveTarget) { setShowProjPicker(false); return }
    setShowProjPicker(false)
    try {
      if (pickerSelected === 'unassigned') {
        await removeSkillFromProject(selectedProject, moveTarget.slug || moveTarget.name)
        setUnassignedCount(c => c + 1)
      } else {
        await addSkillToProject(pickerSelected, moveTarget.slug || moveTarget.name)
      }
      showToast(`${moveTarget.name} moved`)
      loadSkillsForProject(selectedProject)
      if (selectedSkill?.id === moveTarget.id) setSelectedSkill(null)
    } catch { showToast('Move failed') }
    setMoveTarget(null)
  }

  const handleArchive = async (sk?: SkillRow) => {
    const target = sk || selectedSkill
    if (!target) return
    try {
      await archiveSkill(target.slug || target.name)
      setSkills(prev => prev.filter(s => s.id !== target.id))
      if (selectedSkill?.id === target.id) setSelectedSkill(null)
      showToast(`${target.name} archived`)
      if (selectedProject === 'unassigned') setUnassignedCount(c => Math.max(0, c - 1))
    } catch { showToast('Archive failed') }
    setShowArchiveConfirm(false)
  }

  const handlePermanentDelete = async () => {
    const target = selectedSkill
    if (!target) return
    try {
      await permanentlyDeleteSkill(target.slug || target.name)
      setSkills(prev => prev.filter(s => s.id !== target.id))
      if (selectedSkill?.id === target.id) setSelectedSkill(null)
      showToast(`${target.name} permanently deleted`)
    } catch { showToast('Delete failed') }
    setShowDeleteConfirm(false)
    setDeleteConfirmText('')
  }

  const handleDownload = async (sk: SkillRow) => {
    try {
      const res = await getSkill(sk.slug || sk.name)
      const content = res.data.content || `---\nname: ${sk.name}\nversion: ${sk.version}\nstatus: ${sk.status}\n---\n\n${sk.description || ''}\n`
      const a = Object.assign(document.createElement('a'), {
        href: URL.createObjectURL(new Blob([content], { type: 'text/markdown' })),
        download: `${sk.slug || sk.name}.md`
      })
      a.click()
    } catch {
      showToast('Download failed')
    }
  }

  const processUploadedFile = async (file: File | undefined) => {
    if (!file?.name.endsWith('.md')) { showToast('Only .md files are supported'); return }
    const content = await file.text()
    const nameMatch = content.match(/^name:\s*(.+)$/m)
    const skillName = nameMatch?.[1].trim() || file.name.replace('.md', '')
    try {
      await createSkill({ name: skillName, content, description: skillName, status: 'draft', project_id: selectedProject !== 'unassigned' ? selectedProject : null })
      showToast(`${skillName} added`)
      loadSkillsForProject(selectedProject)
    } catch (e: any) {
      showToast(e?.response?.data?.detail || 'Upload failed')
    }
  }

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDzOver(false)
    await processUploadedFile(e.dataTransfer.files?.[0])
  }

  const handleUploadInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    await processUploadedFile(file)
  }

  const loadFolderMembers = useCallback(async (projId: string) => {
    setFolderMembersLoading(true)
    try {
      const res = await listProjectMembers(projId)
      setFolderMembers(res.data || [])
    } catch {
      setFolderMembers([])
    } finally {
      setFolderMembersLoading(false)
    }
  }, [])

  const openFolderMembersPanel = () => {
    setShowFolderMembersPanel(true)
    loadFolderMembers(selectedProject)
  }

  const handleAddFolderMember = async (userId: string) => {
    setFolderMemberAdding(userId)
    try {
      await addProjectMember(selectedProject, { user_id: userId, role: 'member' })
      await loadFolderMembers(selectedProject)
    } catch {
      showToast('Failed to add member')
    } finally {
      setFolderMemberAdding(null)
    }
  }

  const handleRemoveFolderMember = async (userId: string) => {
    setFolderMemberRemoving(userId)
    try {
      await removeProjectMember(selectedProject, userId)
      await loadFolderMembers(selectedProject)
    } catch {
      showToast('Failed to remove member')
    } finally {
      setFolderMemberRemoving(null)
    }
  }

  const handleNewProject = async () => {
    if (!npName.trim()) return
    setNpSaving(true)
    try {
      await createProject({ name: npName.trim(), description: npDesc.trim() || undefined })
      showToast(`Project "${npName}" created`)
      setShowNewProject(false); setNpName(''); setNpDesc('')
      await loadGroups()
    } catch { showToast('Failed to create project') }
    finally { setNpSaving(false) }
  }

  const handleInvite = async () => {
    if (!orgId || !invEmail.trim()) return
    setInvSaving(true)
    try {
      const role = invRole === 'admin' ? 'admin' : 'member'
      await inviteOrgMember(orgId, { email: invEmail.trim(), role })
      showToast(`Invite sent to ${invEmail.trim()}`)
      setShowInvite(false)
      setInvEmail('')
      setInvRole('editor')
    } catch (e: any) {
      showToast(e?.response?.data?.detail || 'Failed to send invite')
    } finally {
      setInvSaving(false)
    }
  }

  // ── Filtered / sorted list ────────────────────────────────────────────────

  const filteredSkills = skills.filter(sk => {
    if (filter !== 'all' && sk.status !== filter) return false
    const q = search.toLowerCase()
    if (q && !sk.name.toLowerCase().includes(q) && !(sk.description || '').toLowerCase().includes(q)) return false
    return true
  }).sort((a, b) => {
    if (sort === 'name') return a.name.localeCompare(b.name)
    if (sort === 'status') { const o: Record<string,number> = { production: 0, staging: 1, draft: 2, archived: 3 }; return (o[a.status]??2) - (o[b.status]??2) }
    return (b.updated_at || '').localeCompare(a.updated_at || '')
  })

  const filteredMembers = members.filter(m => {
    if (teamSection === 'pending') return false
    return true
  })

  const projName = (id: string) => {
    let result: string
    if (id === 'unassigned') result = 'Unassigned'
    else {
      result = '—'
      for (const g of groups) { const p = g.projects.find(p => p.id === id); if (p) { result = p.name; break } }
    }
    return result
  }

  const p3Open = !!(selectedSkill || selectedUser)

  // ── Layout constants ──────────────────────────────────────────────────────

  const COL_SKILLS  = '22px 1fr 116px 70px 160px 120px 104px'
  const COL_MEMBERS = '44px 1fr 200px 90px 90px 120px 80px'

  const cellStyle: React.CSSProperties = { display: 'contents' }
  const th: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }

  // Sidebar nav data, derived from the existing groups/skill_count model
  const sidebarTeams = groups.map(g => ({ id: g.team_id, name: g.team_name }))
  // Public projects get their own top-level "Public" nav entry — keep them out of MY PROJECTS.
  // Projects where the caller has a project_members row but doesn't own the project
  // (owner_id present and not theirs) get their own "Shared with me" entry instead of
  // being silently folded into MY PROJECTS — that's what made David Clark's SEO/AEO
  // membership invisible even after being added.
  const isSharedWithMe = (p: Project) => !p.is_public && !!p.owner_id && p.owner_id !== user?.id
  const sidebarProjects = groups.flatMap(g => g.projects.filter(p => !p.is_public && !isSharedWithMe(p)).map(p => ({ id: p.id, name: p.name, team_name: g.team_name })))
  const publicProjects = groups.flatMap(g => g.projects.filter(p => p.is_public).map(p => ({ id: p.id, name: p.name })))
  const sharedProjects = groups.flatMap(g => g.projects.filter(isSharedWithMe).map(p => ({ id: p.id, name: p.name })))
  const sidebarProjectCounts = Object.fromEntries(groups.flatMap(g => g.projects.map(p => [p.id, p.skill_count])))

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--surface2)' }}>
      <HomeSidebar
        teams={sidebarTeams}
        allProjects={sidebarProjects}
        publicProjects={publicProjects}
        sharedProjects={sharedProjects}
        selectedProject={selectedProject}
        projectCounts={sidebarProjectCounts}
        teamContext={teamContext}
        onSelectTeam={setTeamContext}
        onSelectProject={handleSelectProject}
        userEmail={user?.email}
        onSignOut={async () => { await signOut(); navigate('/login') }}
      />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav style={{ height: 64, flexShrink: 0, background: 'var(--bg-1)', borderBottom: '1px solid var(--mitel-gray-20)', display: 'flex', alignItems: 'center', padding: '0 40px', gap: 10, zIndex: 200 }}>
        {/* Breadcrumb */}
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--mitel-dark-blue)', whiteSpace: 'nowrap' }}>Skilflow</span>
        <span style={{ color: 'var(--mitel-gray-50)', fontSize: 15 }}>/</span>
        <span style={{ fontSize: 15, color: 'var(--fg-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{projName(selectedProject)}</span>

        <div style={{ flex: 1 }} />

        {/* Pill search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-2)', border: '1px solid var(--mitel-gray-20)', borderRadius: 'var(--r-pill)', padding: '7px 7px 7px 16px', width: 240 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}><circle cx="11" cy="11" r="7" stroke="var(--mitel-gray)" strokeWidth="1.7" /><path d="M21 21l-4.3-4.3" stroke="var(--mitel-gray)" strokeWidth="1.7" strokeLinecap="round" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search skills…" style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: 'var(--mitel-dark-blue)', fontFamily: 'inherit', background: 'transparent', minWidth: 0 }} />
        </div>

        {/* Member avatars + count */}
        {membersLoading ? (
          // Skeleton cluster — same footprint as the real one below, so the nav
          // bar never renders with a bare gap while listOrgs → loadMembers is
          // still in flight (was previously indistinguishable from no cluster
          // at all, i.e. the pre-756dafa toolbar).
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px' }}>
            <div style={{ display: 'flex' }}>
              {[0, 1, 2].map(i => (
                <div key={i} className="sk-cell" style={{ width: 28, height: 28, borderRadius: '50%', marginLeft: i === 0 ? 0 : -8, border: '2px solid var(--bg-1)' }} />
              ))}
            </div>
            <div className="sk-cell" style={{ height: 11, width: 64, borderRadius: 4 }} />
          </div>
        ) : members.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Clickable cluster → opens members panel */}
            <button
              onClick={() => setShowMembersPanel(true)}
              title="View members"
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 8 }}
            >
              <div style={{ display: 'flex' }}>
                {members.slice(0, 3).map((m, i) => (
                  <div key={m.user_id} style={{ width: 28, height: 28, borderRadius: '50%', background: avColor(m.display_name || m.email || m.user_id), color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: i === 0 ? 0 : -8, border: '2px solid var(--bg-1)', flexShrink: 0 }}>
                    {memberInitials(m)}
                  </div>
                ))}
              </div>
              <span style={{ fontSize: 13, color: 'var(--fg-3)', fontWeight: 600, whiteSpace: 'nowrap' }}>Members · {members.length}</span>
            </button>
            {/* + invite button */}
            <button onClick={() => setShowInvite(true)} title="Invite member" style={{ width: 28, height: 28, borderRadius: '50%', border: '1.5px solid var(--mitel-gray-20)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: 'var(--fg-3)', fontSize: 18, lineHeight: 1 }}>+</button>
          </div>
        )}

      </nav>

      {/* ── App body ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Panel 2 ───────────────────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {/* P2 header */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
            <input ref={uploadInputRef} type="file" accept=".md" style={{ display: 'none' }} onChange={handleUploadInputChange} />
            {view === 'projects' ? (
              <>
                <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--text1)' }}>{projName(selectedProject)}</span>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>{filteredSkills.length} skill{filteredSkills.length !== 1 ? 's' : ''}</span>
                <div style={{ width: 1, height: 16, background: 'var(--border)', flexShrink: 0 }} />
                {membersLoading ? (
                  // Skeleton — org membership isn't known yet, so we can't tell
                  // whether this folder gets the member-pill or filter-tabs
                  // header. Rendering the filter-tabs header by default here
                  // was the real "old toolbar" flash: it showed unconditionally
                  // while members loaded, then snapped to the member-pill
                  // header for any org with >1 member (e.g. the Public folder
                  // on every page load).
                  <>
                    <div className="sk-cell" style={{ height: 28, width: 92, borderRadius: 999 }} />
                    <div className="sk-cell" style={{ height: 26, width: 120, borderRadius: 6 }} />
                    <div style={{ marginLeft: 'auto' }}>
                      <div className="sk-cell" style={{ height: 26, width: 76, borderRadius: 6 }} />
                    </div>
                  </>
                ) : members.length > 1 ? (
                  <>
                    {/* Member pill */}
                    <div onClick={openFolderMembersPanel} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e2e8f0', borderRadius: 999, padding: '4px 12px 4px 4px', cursor: 'pointer' }}>
                      <div style={{ display: 'flex' }}>
                        {members.slice(0, 3).map((m, i) => (
                          <div key={m.user_id || i} style={{ width: 28, height: 28, borderRadius: '50%', background: AVATAR_PALETTE[i % AVATAR_PALETTE.length], color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: i === 0 ? 0 : -8, border: '2px solid var(--surface)', flexShrink: 0 }}>
                            {memberInitials(m)}
                          </div>
                        ))}
                        {members.length > 3 && (
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--mitel-gray-20)', color: 'var(--text2)', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: -8, border: '2px solid var(--surface)', flexShrink: 0 }}>
                            +{members.length - 3}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600, whiteSpace: 'nowrap' }}>{members.length} members</span>
                    </div>
                    <select value={sort} onChange={e => setSort(e.target.value as any)}
                      style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: 'var(--text2)', background: 'var(--surface)', fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}>
                      <option value="default">Last modified</option>
                      <option value="name">Name A–Z</option>
                      <option value="status">Status</option>
                    </select>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <button onClick={() => uploadInputRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: 'none', background: 'var(--mitel-mid-blue)', cursor: 'pointer', color: '#fff', fontFamily: 'inherit' }}>
                        ↑ Upload
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {(['all','production','staging','draft'] as const).map(f => (
                      <button key={f} onClick={() => setFilter(f)}
                        style={{ padding: '4px 11px', borderRadius: 12, fontSize: 11, fontWeight: 600, border: `1px solid ${filter === f ? 'var(--brand)' : 'transparent'}`, cursor: 'pointer', background: filter === f ? 'var(--brand)' : 'transparent', color: filter === f ? '#000' : 'var(--text3)', fontFamily: 'inherit', boxShadow: filter === f ? '0 1px 4px rgba(253,183,20,0.25)' : 'none' }}>
                        {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                    <select value={sort} onChange={e => setSort(e.target.value as any)}
                      style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: 'var(--text2)', background: 'var(--surface)', fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}>
                      <option value="default">Last modified</option>
                      <option value="name">Name A–Z</option>
                      <option value="status">Status</option>
                    </select>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <button onClick={() => setShowWhatsNew(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontFamily: 'inherit' }}>
                        ⚡ What's new
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--text1)' }}>Members</span>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>{members.length} members</span>
                <div style={{ marginLeft: 'auto' }}>
                  <button onClick={() => setShowInvite(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--brand)', background: 'var(--brand)', cursor: 'pointer', color: '#000', fontFamily: 'inherit' }}>
                    + Invite member
                  </button>
                </div>
              </>
            )}
          </div>

          {/* P2 scroll area */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {view === 'projects' ? (
              <>
                {/* Column header */}
                <div style={{ display: 'grid', gridTemplateColumns: COL_SKILLS, padding: '0 16px', height: 34, alignItems: 'center', borderBottom: '1px solid var(--border)', background: 'var(--bg)', position: 'sticky', top: 0, zIndex: 10 }}>
                  <div /><div style={th}>Name</div><div style={th}>Status</div><div style={th}>Version</div><div style={th}>Owner</div><div style={th}>Updated</div><div style={th}>Actions</div>
                </div>
                {/* Drop zone */}
                <div
                  onDragOver={e => { e.preventDefault(); if (e.dataTransfer.types.includes('Files')) setDzOver(true) }}
                  onDragLeave={() => setDzOver(false)}
                  onDrop={handleFileDrop}
                  style={{ margin: '8px 16px', padding: '14px 20px', border: `1.5px dashed ${dzOver ? 'var(--brand)' : 'var(--border)'}`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, fontSize: 12, color: dzOver ? 'var(--brand)' : 'var(--text3)', background: dzOver ? 'rgba(253,183,20,0.08)' : 'transparent', cursor: 'default' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M7 10V2M3 6l4 4 4-4M1 12h12"/></svg>
                  Drop a .md file here to add a new skill
                </div>
                {/* Skill rows */}
                {skillsLoading ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>Loading…</div>
                ) : filteredSkills.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 180, gap: 8, color: 'var(--text3)' }}>
                    <span style={{ fontSize: 28, opacity: 0.5 }}>🔭</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>No skills here</span>
                    <span style={{ fontSize: 12 }}>Drop a .md file or add via MCP</span>
                  </div>
                ) : filteredSkills.map(sk => {
                  const skKey = sk.slug || sk.name
                  const isBulkSelected = selectedSkills.has(skKey)
                  const isInFlight = draggingInFlight.has(skKey)
                  const sel = selectedSkill?.id === sk.id && !isBulkSelected
                  const staggerIdx = newSkillAnimations.get(sk.id)
                  return (
                    <div key={sk.id}
                      onClick={e => { if (e.metaKey || e.ctrlKey) { setSelectedSkills(prev => { const n = new Set(prev); n.has(skKey) ? n.delete(skKey) : n.add(skKey); return n }); setNewSkillAnimations(prev => { const n = new Map(prev); n.delete(sk.id); return n }) } else { openSkill(sk) } }}
                      className={staggerIdx !== undefined ? 'skill-row-enter' : undefined}
                      style={{ display: 'grid', gridTemplateColumns: COL_SKILLS, padding: '0 16px', height: 44, alignItems: 'center', borderBottom: '1px solid var(--border)', borderLeft: `3px solid ${isBulkSelected ? '#0e9b87' : sel ? 'var(--brand)' : 'transparent'}`, cursor: 'pointer', background: isBulkSelected ? 'rgba(14,155,135,0.06)' : sel ? 'rgba(253,183,20,0.04)' : 'transparent', transition: 'background 0.1s, opacity 0.15s', opacity: isInFlight ? 0.4 : 1, animationDelay: staggerIdx !== undefined ? `${staggerIdx * 60}ms` : undefined }}
                      onMouseEnter={e => { if (!sel && !isBulkSelected) e.currentTarget.style.background = 'var(--bg)' }}
                      onMouseLeave={e => { if (!sel && !isBulkSelected) e.currentTarget.style.background = 'transparent' }}>
                      {/* drag handle — draggable scoped here so CMD+click on the row reaches onClick */}
                      <div draggable
                        onDragStart={e => { const slug = sk.slug || sk.name; const slugs = selectedSkills.has(skKey) && selectedSkills.size > 1 ? Array.from(selectedSkills) : [slug]; dragSkillRef.current = slugs; e.dataTransfer.setData('text/plain', slugs.join(',')); e.dataTransfer.effectAllowed = 'move' }}
                        onDragEnd={() => { dragSkillRef.current = [] }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', opacity: 0, fontSize: 11, cursor: 'grab' }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0')}>⠿</div>
                      {/* name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <div style={{ width: 17, height: 17, borderRadius: '50%', background: 'rgba(129,44,196,0.08)', color: 'var(--purple)', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>C</div>
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{sk.name}</span>
                      </div>
                      {/* status */}
                      <div><span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 600, ...statusStyle(sk.status) }}>{statusLabel(sk.status)}</span></div>
                      {/* version */}
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'JetBrains Mono, monospace' }}>{sk.version}</div>
                      {/* owner */}
                      <div style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sk.owner || '—'}</div>
                      {/* updated */}
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>{timeAgo(sk.updated_at)}</div>
                      {/* row actions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, opacity: 0 }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0')}>
                        {[
                          { label: 'Move', action: (e: React.MouseEvent) => { e.stopPropagation(); openMovePicker(sk) } },
                          { label: 'Pub',  action: (e: React.MouseEvent) => { e.stopPropagation(); setSelectedSkill(sk); setShowPublish(true) } },
                          ...(sk.status === 'archived'
                            ? [{ label: 'Delete', action: (e: React.MouseEvent) => { e.stopPropagation(); setSelectedSkill(sk); setDeleteConfirmText(''); setShowDeleteConfirm(true) } }]
                            : [{ label: 'Archive', action: (e: React.MouseEvent) => { e.stopPropagation(); setSelectedSkill(sk); setShowArchiveConfirm(true) } }]),
                        ].map(btn => (
                          <button key={btn.label} onClick={btn.action}
                            style={{ padding: '3px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontFamily: 'inherit' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)'; e.currentTarget.style.background = 'rgba(253,183,20,0.08)' }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'var(--surface)' }}>
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
                {/* "Loading more…" indicator — shown while background batches are in flight */}
                {skills.length > 0 && skills.length < totalSkills && !skillsLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div className="loading-more-dot" />
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>Loading more…</span>
                  </div>
                )}
                {/* Skeleton rows for unloaded items — only shown with no active filter/search */}
                {filter === 'all' && !search && Array.from({ length: Math.max(0, totalSkills - skills.length) }).map((_, i) => (
                  <div key={`sk-${i}`} style={{ display: 'grid', gridTemplateColumns: COL_SKILLS, padding: '0 16px', height: 44, alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                    <div />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="sk-cell" style={{ width: 17, height: 17, borderRadius: '50%' }} />
                      <div className="sk-cell" style={{ height: 11, width: `${40 + (i % 5) * 12}%` }} />
                    </div>
                    <div><div className="sk-cell" style={{ height: 18, width: 76, borderRadius: 10 }} /></div>
                    <div><div className="sk-cell" style={{ height: 11, width: 34 }} /></div>
                    <div><div className="sk-cell" style={{ height: 11, width: '70%' }} /></div>
                    <div><div className="sk-cell" style={{ height: 11, width: 52 }} /></div>
                    <div />
                  </div>
                ))}
              </>
            ) : (
              /* Member table */
              <>
                <div style={{ display: 'grid', gridTemplateColumns: COL_MEMBERS, padding: '0 16px', height: 34, alignItems: 'center', borderBottom: '1px solid var(--border)', background: 'var(--bg)', position: 'sticky', top: 0, zIndex: 10 }}>
                  <div /><div style={th}>Member</div><div style={th}>Email</div><div style={th}>Role</div><div style={th}>Projects</div><div style={th}>Last Active</div><div />
                </div>
                {teamLoading ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>Loading…</div>
                ) : filteredMembers.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 180, gap: 8, color: 'var(--text3)' }}>
                    <span style={{ fontSize: 28, opacity: 0.5 }}>✉</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>No members</span>
                    <span style={{ fontSize: 12 }}>Invite teammates to collaborate</span>
                  </div>
                ) : filteredMembers.map(m => {
                  const sel = selectedUser?.user_id === m.user_id
                  const name = memberName(m) || m.user_id
                  return (
                    <div key={m.user_id} onClick={() => setSelectedUser(sel ? null : m)}
                      style={{ display: 'grid', gridTemplateColumns: COL_MEMBERS, padding: '0 16px', height: 52, alignItems: 'center', borderBottom: '1px solid var(--border)', borderLeft: `3px solid ${sel ? 'var(--brand)' : 'transparent'}`, cursor: 'pointer', background: sel ? 'rgba(253,183,20,0.04)' : 'transparent' }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = 'var(--bg)' }}
                      onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent' }}>
                      <Avatar name={name} email={m.email} size={36} />
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{name}</div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email || '—'}</div>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, textTransform: 'uppercase', letterSpacing: '0.05em', background: 'rgba(129,44,196,0.12)', color: '#812cc4' }}>{m.role || 'member'}</span>
                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>—</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>—</div>
                      <div />
                    </div>
                  )
                })}
              </>
            )}
          </div>
        </div>

        {/* ── Panel 3 ───────────────────────────────────────────────────── */}
        <div style={{ width: p3Open ? 320 : 0, flexShrink: 0, overflow: 'hidden', borderLeft: p3Open ? '1px solid var(--border)' : '0px solid var(--border)', background: 'var(--surface)', transition: 'width 0.22s ease, border-left-width 0.22s ease', display: 'flex', flexDirection: 'column' }}>
          <div style={{ width: 320, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* P3 header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px 11px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--text1)', flex: 1, marginRight: 8, wordBreak: 'break-all' }}>
                {selectedSkill?.name || (selectedUser && memberName(selectedUser)) || ''}
              </span>
              <button onClick={() => { setSelectedSkill(null); setSelectedUser(null) }}
                style={{ width: 24, height: 24, borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, color: 'var(--text3)', flexShrink: 0 }}>×</button>
            </div>
            {/* P3 body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {selectedSkill && (
                <>
                  {/* Metadata section */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Metadata</div>
                    {selectedSkill.description && <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 12 }}>{selectedSkill.description}</p>}
                    {[
                      { k: 'Status', v: <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, ...statusStyle(selectedSkill.status) }}>{statusLabel(selectedSkill.status)}</span> },
                      { k: 'Version', v: <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{selectedSkill.version}</span> },
                      { k: 'Owner',   v: selectedSkill.owner || '—' },
                      { k: 'Project', v: projName(selectedProject) },
                    ].map(row => (
                      <div key={row.k} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: 'var(--text3)', minWidth: 58, paddingTop: 2, flexShrink: 0 }}>{row.k}</span>
                        <span style={{ fontSize: 12, color: 'var(--text1)', fontWeight: 500, textAlign: 'right', flex: 1 }}>{row.v}</span>
                      </div>
                    ))}
                    {selectedSkill.tags && selectedSkill.tags.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: 11, color: 'var(--text3)', minWidth: 58, paddingTop: 2, flexShrink: 0 }}>Tags</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                          {selectedSkill.tags.map(t => <span key={t} style={{ fontSize: 10, fontWeight: 500, color: 'var(--text3)', background: 'var(--bg)', padding: '2px 7px', borderRadius: 5 }}>{t}</span>)}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Version history section */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Version History</div>
                    {historyLoading ? (
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading…</div>
                    ) : skillHistory.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>No history available.</div>
                    ) : skillHistory.slice(0, 6).map((h, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '8px 0', borderBottom: i < Math.min(skillHistory.length, 6) - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', marginTop: 4, flexShrink: 0, background: i === 0 ? 'var(--brand)' : 'var(--purple)' }} />
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text1)' }}>{h.message || `Version ${h.version || ''}`}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{h.committed_date ? timeAgo(h.committed_date) : ''}{h.author ? ` · ${h.author}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {selectedUser && !selectedSkill && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <Avatar name={isPendingMember(selectedUser) ? undefined : selectedUser.display_name} email={selectedUser.email} size={44} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text1)' }}>{memberName(selectedUser)}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>{selectedUser.email}</div>
                      <div style={{ marginTop: 6 }}><span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, textTransform: 'uppercase', letterSpacing: '0.05em', background: 'rgba(129,44,196,0.12)', color: '#812cc4' }}>{selectedUser.role || 'member'}</span></div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
                    Member profile details coming in a future update.
                  </div>
                </>
              )}
            </div>
            {/* P3 actions */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {selectedSkill && (
                <>
                  <button onClick={() => openMovePicker(selectedSkill)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1px solid var(--brand)', background: 'var(--brand)', cursor: 'pointer', color: '#000', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                    → Move to project
                  </button>
                  <button onClick={() => { navigate(`/skills/${selectedSkill.slug || selectedSkill.name}`) }}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                    ↗ Open detail view
                  </button>
                  {selectedSkill.file_type !== 'document' && (
                    <button onClick={() => setShowPublish(true)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                      ↑ Publish to production
                    </button>
                  )}
                  <button onClick={() => handleDownload(selectedSkill)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                    ↓ Download .md
                  </button>
                  <button onClick={() => setShowArchiveConfirm(true)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)'; e.currentTarget.style.color = '#dc2626' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)' }}>
                    ☐ Archive
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid var(--border)', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', flexShrink: 0 }}>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>© 2026 Skilflow · v1.0</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: 'var(--text3)' }}>
          Powered by <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 13, fontWeight: 700, color: '#1B3A5C' }}>mitel</span>
        </div>
      </footer>

      {/* ── Toast ────────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 48, left: '50%', transform: 'translateX(-50%)', padding: '9px 16px', borderRadius: 8, fontSize: 12, fontWeight: 500, zIndex: 500, background: 'var(--text1)', color: 'var(--bg)', whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          {toast}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MODALS                                                            */}
      {/* ══════════════════════════════════════════════════════════════════ */}

      {/* Project menu backdrop */}
      {projectMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setProjectMenu(null)} />}

      {/* Edit project modal */}
      {editProjectModal && (
        <div className="modal-overlay" onClick={() => setEditProjectModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--text1)', marginBottom: 16 }}>Edit project</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Name</label>
              <input autoFocus value={editProjectModal.name} onChange={e => setEditProjectModal(m => m ? { ...m, name: e.target.value } : m)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)', boxSizing: 'border-box' as const }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Description</label>
              <textarea value={editProjectModal.description} onChange={e => setEditProjectModal(m => m ? { ...m, description: e.target.value } : m)}
                placeholder="Optional"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', resize: 'vertical' as const, minHeight: 68, background: 'var(--surface)', boxSizing: 'border-box' as const }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditProjectModal(null)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={handleEditProjectSave} disabled={editProjectSaving || !editProjectModal.name.trim()}
                style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--brand)', cursor: 'pointer', background: 'var(--brand)', fontFamily: 'inherit', color: '#000', opacity: editProjectSaving || !editProjectModal.name.trim() ? 0.5 : 1 }}>
                {editProjectSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Members panel — slide-in overlay */}
      {showMembersPanel && (
        <>
          <div onClick={() => setShowMembersPanel(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 300 }} />
          <div style={{ position: 'fixed', top: 64, right: 0, bottom: 0, width: 320,
                        background: 'var(--surface)', borderLeft: '1px solid var(--border)',
                        zIndex: 301,
                        display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '13px 16px 11px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--text1)', flex: 1, marginRight: 8 }}>Members</span>
              <button onClick={() => setShowMembersPanel(false)}
                style={{ width: 24, height: 24, borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, color: 'var(--text3)', flexShrink: 0 }}>×</button>
            </div>
            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {teamLoading ? (
                <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', paddingTop: 20 }}>Loading…</div>
              ) : <>
                {/* Confirmed members */}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                    Members ({members.length})
                  </div>
                  {members.map((m, i) => {
                    const isOwner = (m.role || '').toLowerCase() === 'owner' || (m.role || '').toLowerCase() === 'admin'
                    const roleLabel = isOwner ? 'OWNER' : 'MEMBER'
                    const roleColor = isOwner ? 'var(--mitel-dark-blue)' : 'var(--mitel-purple)'
                    const roleBg = isOwner ? 'var(--mitel-pale-blue, rgba(21,50,95,0.10))' : 'rgba(129,44,196,0.10)'
                    const name = memberName(m) || `User ${i+1}`
                    return (
                      <div key={m.user_id || i} style={{ display: 'flex', alignItems: 'center', gap: 9,
                                                          padding: '8px 0', borderBottom: i < members.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%',
                                      background: avColor(m.display_name || m.email || m.user_id || String(i)),
                                      color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {memberInitials(m)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                          {m.email && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>}
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 6px',
                                       borderRadius: 99, background: roleBg, color: roleColor, flexShrink: 0 }}>
                          {roleLabel}
                        </span>
                      </div>
                    )
                  })}
                </div>

                {/* Pending invitations */}
                {orgInvitations.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                      Pending ({orgInvitations.length})
                    </div>
                    {orgInvitations.map((inv, i) => (
                      <div key={inv.id || i} style={{ display: 'flex', alignItems: 'center', gap: 9,
                                                       padding: '8px 0', borderBottom: i < orgInvitations.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%',
                                      background: 'var(--mitel-gray-20)', color: 'var(--text3)',
                                      fontSize: 13, fontWeight: 700, flexShrink: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {(inv.email || '?').slice(0,2).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.email}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>Invite pending</div>
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 6px',
                                       borderRadius: 99, background: 'rgba(251,191,36,0.15)', color: '#92400e', flexShrink: 0 }}>
                          PENDING
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>}
            </div>
            {/* Footer */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <button onClick={() => { setShowMembersPanel(false); setShowInvite(true) }}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                         border: 'none', background: 'var(--mitel-mid-blue)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                + Invite member
              </button>
            </div>
          </div>
        </>
      )}

      {showFolderMembersPanel && (
        <>
          <div onClick={() => setShowFolderMembersPanel(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 300 }} />
          <div style={{ position: 'fixed', top: 64, right: 0, bottom: 0, width: 360,
                        background: 'var(--surface)', borderLeft: '1px solid var(--border)',
                        zIndex: 301,
                        display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '13px 16px 11px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--text1)', flex: 1, marginRight: 8 }}>{projName(selectedProject)} members</span>
              <button onClick={() => setShowFolderMembersPanel(false)}
                style={{ width: 24, height: 24, borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, color: 'var(--text3)', flexShrink: 0 }}>×</button>
            </div>
            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {folderMembersLoading ? (
                <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', paddingTop: 20 }}>Loading…</div>
              ) : <>
                {/* Folder members */}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                    Members ({folderMembers.length})
                  </div>
                  {folderMembers.map((m, i) => {
                    const isOwner = (m.role || '').toLowerCase() === 'owner' || (m.role || '').toLowerCase() === 'admin'
                    const roleLabel = isOwner ? 'OWNER' : 'MEMBER'
                    const roleColor = isOwner ? 'var(--mitel-dark-blue)' : 'var(--mitel-purple)'
                    const roleBg = isOwner ? 'var(--mitel-pale-blue, rgba(21,50,95,0.10))' : 'rgba(129,44,196,0.10)'
                    const name = memberName(m) || `User ${i+1}`
                    return (
                      <div key={m.user_id || i} style={{ display: 'flex', alignItems: 'center', gap: 9,
                                                          padding: '8px 0', borderBottom: i < folderMembers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%',
                                      background: avColor(m.display_name || m.email || m.user_id || String(i)),
                                      color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {memberInitials(m)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                          {m.email && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>}
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 6px',
                                       borderRadius: 99, background: roleBg, color: roleColor, flexShrink: 0 }}>
                          {roleLabel}
                        </span>
                        {!isOwner && (
                          <button onClick={() => handleRemoveFolderMember(m.user_id)}
                            disabled={folderMemberRemoving === m.user_id}
                            title="Remove from folder"
                            style={{ width: 22, height: 22, borderRadius: 5, border: '1px solid var(--border)',
                                     background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                     cursor: folderMemberRemoving === m.user_id ? 'default' : 'pointer',
                                     fontSize: 13, color: 'var(--text3)', flexShrink: 0 }}>
                            {folderMemberRemoving === m.user_id ? '…' : '×'}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Org members not yet in this folder */}
                {(() => {
                  const notInFolder = members.filter(om => !folderMembers.some(fm => fm.user_id === om.user_id))
                  return notInFolder.length > 0 && (
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                        Add member
                      </div>
                      {notInFolder.map((m, i) => {
                        const name = memberName(m) || `User ${i+1}`
                        return (
                          <div key={m.user_id || i} style={{ display: 'flex', alignItems: 'center', gap: 9,
                                                              padding: '8px 0', borderBottom: i < notInFolder.length - 1 ? '1px solid var(--border)' : 'none' }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%',
                                          background: avColor(m.display_name || m.email || m.user_id || String(i)),
                                          color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
                                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {memberInitials(m)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                              {m.email && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>}
                            </div>
                            <button onClick={() => handleAddFolderMember(m.user_id)}
                              disabled={folderMemberAdding === m.user_id}
                              style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                       border: '1px solid var(--border)', background: 'var(--surface)',
                                       cursor: folderMemberAdding === m.user_id ? 'default' : 'pointer',
                                       color: 'var(--text2)', fontFamily: 'inherit', flexShrink: 0 }}>
                              {folderMemberAdding === m.user_id ? '…' : 'Add'}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </>}
            </div>
          </div>
        </>
      )}

      {/* What's New — slide-in panel */}
      {showWhatsNew && (
        <>
          <div onClick={handleCloseWhatsNew}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 300 }} />
          <div className="whats-new-panel"
            style={{ position: 'fixed', top: 52, right: 0, bottom: 0, width: 360,
                     background: 'var(--surface)', borderLeft: '1px solid var(--border)',
                     boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', zIndex: 301,
                     display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                          padding: '16px 20px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div>
                <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--text1)' }}>
                  ⚡ What's new
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Latest updates to Skilflow</div>
              </div>
              <button onClick={handleCloseWhatsNew}
                style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border)',
                         background: 'transparent', cursor: 'pointer', fontSize: 15, color: 'var(--text3)',
                         display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>
                Activity feed coming in a future update.
              </div>
            </div>
          </div>
        </>
      )}

      {/* New Skill info */}
      {showNewSkill && (
        <div className="modal-overlay" onClick={() => setShowNewSkill(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)' }}>Add a skill</span>
              <button onClick={() => setShowNewSkill(false)} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, color: 'var(--text3)' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 14 }}>Skills are added automatically when saved via MCP, or manually by file upload.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {[
                  { icon: '🔭', title: 'Via MCP', desc: 'Save a skill from Claude or Claude Code using the Skilflow MCP connector' },
                  { icon: '📄', title: 'Drag & drop', desc: 'Drop a .md file directly onto the skill list in any project' },
                  { icon: '✏️', title: 'AI creator', action: () => { setShowNewSkill(false); navigate('/skills/new') }, desc: 'Use the guided skill creator to build a new skill with AI' },
                ].map(item => (
                  <div key={item.title} onClick={item.action} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', cursor: item.action ? 'pointer' : 'default' }}>
                    <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)', marginBottom: 2 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
              <button onClick={() => setShowNewSkill(false)} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--brand)', cursor: 'pointer', background: 'var(--brand)', fontFamily: 'inherit', color: '#000' }}>Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* New Project */}
      {showNewProject && (
        <div className="modal-overlay" onClick={() => setShowNewProject(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)' }}>Create project</span>
              <button onClick={() => setShowNewProject(false)} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, color: 'var(--text3)' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {[
                { label: 'Project name *', el: <input value={npName} onChange={e => setNpName(e.target.value)} placeholder="e.g. Campaign Assets" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)', boxSizing: 'border-box' as const }} /> },
                { label: 'Description', el: <textarea value={npDesc} onChange={e => setNpDesc(e.target.value)} placeholder="What skills belong in this project?" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', resize: 'vertical' as const, minHeight: 68, background: 'var(--surface)', boxSizing: 'border-box' as const }} /> },
                ].map(row => (
                <div key={row.label} style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{row.label}</label>
                  {row.el}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
              <button onClick={() => setShowNewProject(false)} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={handleNewProject} disabled={npSaving || !npName.trim()}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--brand)', cursor: 'pointer', background: 'var(--brand)', fontFamily: 'inherit', color: '#000', opacity: npSaving || !npName.trim() ? 0.5 : 1 }}>
                {npSaving ? 'Creating…' : 'Create project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite */}
      {showInvite && (
        <div className="modal-overlay" onClick={() => setShowInvite(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)' }}>Invite member</span>
              <button onClick={() => setShowInvite(false)} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, color: 'var(--text3)' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Email address</label>
                <input type="email" value={invEmail} onChange={e => setInvEmail(e.target.value)} placeholder="colleague@company.com" autoFocus
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)', boxSizing: 'border-box' as const }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Role</label>
                <select value={invRole} onChange={e => setInvRole(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)', cursor: 'pointer' }}>
                  <option value="editor">Editor — can create and edit skills</option>
                  <option value="viewer">Viewer — can view and use skills</option>
                  <option value="admin">Admin — full team access</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
              <button onClick={() => setShowInvite(false)} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={handleInvite} disabled={invSaving || !invEmail.trim()}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--brand)', cursor: 'pointer', background: 'var(--brand)', fontFamily: 'inherit', color: '#000', opacity: invSaving || !invEmail.trim() ? 0.5 : 1 }}>
                {invSaving ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Picker (Move) */}
      {showProjPicker && (
        <div className="modal-overlay" onClick={() => setShowProjPicker(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)' }}>Move to project</span>
              <button onClick={() => setShowProjPicker(false)} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, color: 'var(--text3)' }}>×</button>
            </div>
            <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} placeholder="Search projects…"
              style={{ width: '100%', padding: '10px 16px', border: 'none', borderBottom: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)' }} />
            <div style={{ maxHeight: 320, overflowY: 'auto', padding: '6px 0' }}>
              {/* Unassigned option */}
              <div onClick={() => setPickerSelected('unassigned')}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 20px', cursor: 'pointer', background: pickerSelected === 'unassigned' ? 'rgba(253,183,20,0.14)' : 'transparent' }}
                onMouseEnter={e => { if (pickerSelected !== 'unassigned') e.currentTarget.style.background = 'var(--bg)' }}
                onMouseLeave={e => { if (pickerSelected !== 'unassigned') e.currentTarget.style.background = 'transparent' }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${pickerSelected === 'unassigned' ? 'var(--brand)' : 'var(--border)'}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: pickerSelected === 'unassigned' ? 'var(--brand)' : 'transparent' }}>
                  {pickerSelected === 'unassigned' && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#000' }} />}
                </div>
                <span style={{ fontSize: 13, color: 'var(--text1)' }}>Unassigned</span>
              </div>
              {groups.map(g => {
                const projs = g.projects.filter(p => p.id !== pickerFrom && (!pickerSearch || p.name.toLowerCase().includes(pickerSearch.toLowerCase())))
                if (!projs.length) return null
                return (
                  <div key={g.team_id}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '8px 20px 4px' }}>{g.team_name}</div>
                    {projs.map(p => (
                      <div key={p.id} onClick={() => setPickerSelected(p.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 20px', cursor: 'pointer', background: pickerSelected === p.id ? 'rgba(253,183,20,0.14)' : 'transparent' }}
                        onMouseEnter={e => { if (pickerSelected !== p.id) e.currentTarget.style.background = 'var(--bg)' }}
                        onMouseLeave={e => { if (pickerSelected !== p.id) e.currentTarget.style.background = 'transparent' }}>
                        <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${pickerSelected === p.id ? 'var(--brand)' : 'var(--border)'}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: pickerSelected === p.id ? 'var(--brand)' : 'transparent' }}>
                          {pickerSelected === p.id && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#000' }} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, color: 'var(--text1)' }}>{p.name}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{p.skill_count} skills</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
              <button onClick={() => setShowProjPicker(false)} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={confirmMove} disabled={!pickerSelected}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--brand)', cursor: 'pointer', background: 'var(--brand)', fontFamily: 'inherit', color: '#000', opacity: !pickerSelected ? 0.4 : 1 }}>Move</button>
            </div>
          </div>
        </div>
      )}

      {/* Archive confirm */}
      {showArchiveConfirm && selectedSkill && (
        <div className="modal-overlay" onClick={() => setShowArchiveConfirm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)' }}>Archive skill?</span>
            </div>
            <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
              Archive <strong style={{ color: 'var(--text1)' }}>{selectedSkill.name}</strong>? It will be hidden from active lists but not deleted.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
              <button onClick={() => setShowArchiveConfirm(false)} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={() => handleArchive()} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid rgba(220,38,38,0.3)', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', color: '#dc2626' }}>Archive</button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent delete confirm */}
      {showDeleteConfirm && selectedSkill && (
        <div className="modal-overlay" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText('') }}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)' }}>Permanently delete skill?</span>
            </div>
            <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
              This removes <strong style={{ color: 'var(--text1)' }}>{selectedSkill.name}</strong> and its history for good. This cannot be undone.
              <div style={{ marginTop: 12 }}>
                Type <strong style={{ color: 'var(--text1)' }}>{selectedSkill.name}</strong> to confirm:
              </div>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                autoFocus
                style={{ marginTop: 8, width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text1)', fontSize: 13, fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText('') }} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={handlePermanentDelete} disabled={deleteConfirmText !== selectedSkill.name}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid rgba(220,38,38,0.3)', cursor: 'pointer', background: deleteConfirmText === selectedSkill.name ? 'rgba(220,38,38,0.1)' : 'transparent', fontFamily: 'inherit', color: '#dc2626', opacity: deleteConfirmText === selectedSkill.name ? 1 : 0.4 }}>Delete permanently</button>
            </div>
          </div>
        </div>
      )}

      {/* Publish */}
      {showPublish && selectedSkill && (
        <PublishModal
          skillId={selectedSkill.id || selectedSkill.name}
          destinations={{ openwebui: true, claude: false }}
          onClose={() => setShowPublish(false)}
          onPublished={() => {
            setSkills(prev => prev.map(s => s.id === selectedSkill.id ? { ...s, status: 'production' } : s))
            setSelectedSkill(prev => prev ? { ...prev, status: 'production' } : prev)
            setShowPublish(false)
            showToast(`${selectedSkill.name} published`)
          }}
        />
      )}
    </div>
    </div>
  )
}
